"use client";

import {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

type MenuItem = {
  key: string;
  label: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
};

/**
 * Overflow menu that portals to document.body with position:fixed so table
 * overflow / card clipping cannot cut off the panel.
 */
export function UsersRowMenu({
  label,
  open,
  onOpenChange,
  items,
}: {
  label: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: MenuItem[];
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) {
      setCoords(null);
      return;
    }
    const rect = btnRef.current.getBoundingClientRect();
    const menuWidth = 200;
    const pad = 8;
    let left = rect.right - menuWidth;
    if (left < pad) left = pad;
    if (left + menuWidth > window.innerWidth - pad) {
      left = window.innerWidth - menuWidth - pad;
    }
    let top = rect.bottom + 4;
    const approxHeight = Math.min(items.length, 8) * 36 + 16;
    if (top + approxHeight > window.innerHeight - pad) {
      top = Math.max(pad, rect.top - approxHeight - 4);
    }
    setCoords({ top, left });
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      onOpenChange(false);
    };
    const onReposition = () => onOpenChange(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, onOpenChange]);

  let portal: ReactNode = null;
  if (open && coords && typeof document !== "undefined") {
    portal = createPortal(
      <div
        ref={menuRef}
        className="users-menu users-menu--portal"
        role="menu"
        style={{ top: coords.top, left: coords.left }}
      >
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            role="menuitem"
            className={item.danger ? "danger" : undefined}
            disabled={item.disabled}
            onClick={() => {
              onOpenChange(false);
              item.onSelect();
            }}
          >
            {item.label}
          </button>
        ))}
      </div>,
      document.body,
    );
  }

  return (
    <div className="users-menu-wrap">
      <button
        ref={btnRef}
        type="button"
        className="icon-button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
      >
        <MoreVertical size={16} />
      </button>
      {portal}
    </div>
  );
}
